import React from 'react';
import PropTypes from 'prop-types';
import cx from 'classnames';

import announce from '../../../helpers/announce';

import Label from '../Label';

export default class TextArea extends React.PureComponent {
    static propTypes = {
        /** html id required to connect the input with the label */
        id: PropTypes.string.isRequired,

        /** Label contents, usually text */
        label: PropTypes.node.isRequired,

        /** If false, the label will be screen reader only */
        isLabelVisible: PropTypes.bool,

        /** Text to be used for the input placeholder */
        placeholder: PropTypes.string,

        /** The input's value. Use only when the input is controlled */
        value: PropTypes.string,

        /** Function fired when the field changes */
        onChange: PropTypes.func,

        /** Function fired when the field is blurred */
        onBlur: PropTypes.func,

        /** A function that accepts the field's value and returns an object with
        specific validation-related keys */
        validator: PropTypes.func,

        /** Disables the field */
        isDisabled: PropTypes.bool,

        /** Marks the field as required; Adds required styling to label */
        isRequired: PropTypes.bool,

        /** Marks and styles the field as invalid */
        isInvalid: PropTypes.bool,

        /** Contents of the error message, usually text */
        errorText: PropTypes.node,

        /** Ref to be passed to the input */
        innerRef: PropTypes.oneOfType([PropTypes.func, PropTypes.object, PropTypes.node]),

        /** If true, the input will gain focus when it mounts */
        shouldFocusOnMount: PropTypes.bool,

        /** Enables validation as you type */
        shouldValidateOnUpdate: PropTypes.bool,

        /** Additional className added to outer container for custom styling */
        className: PropTypes.string,

        /** Callback invoked when isInvalid/errorText changes state. Returns an
        object with the id, isValid, and errorText */
        handleNewErrorState: PropTypes.func
    };

    static defaultProps = {
        isDisabled: false,
        isLabelVisible: true,
        value: undefined,
        placeholder: undefined,
        isRequired: false,
        isInvalid: false,
        shouldValidateOnUpdate: false,
        errorText: undefined,
        innerRef: undefined,
        className: undefined
    };

    constructor(props) {
        super(props);

        const hasErrorText = typeof props.errorText === 'string' && props.errorText.length > 0;

        this.state = {
            isPristine: !props.value,
            isInvalid: props.isInvalid || hasErrorText || false,
            errorText: props.errorText || undefined,
        };

        this.errorState = {
            isInvalid: props.isInvalid,
            errorText: props.errorText
        };

        this.inputWrapperRef = React.createRef();
    }

    componentDidMount() {
        const { shouldFocusOnMount } = this.props;

        this.timeout = setTimeout(() => {
            if (shouldFocusOnMount && this.inputWrapperRef) {
                this.inputWrapperRef.current.querySelector('textarea').focus();
            }
        }, 0);
    }

    componentDidUpdate(prevProps) {
        const { shouldValidateOnUpdate, isInvalid, errorText } = this.props;

        if (shouldValidateOnUpdate) {
            this.validate();
        }
        else if (isInvalid !== prevProps.isInvalid || errorText !== prevProps.errorText) {
            const hasErrorText = typeof errorText === 'string' && errorText.length > 0;

            this.setState({
                isInvalid: isInvalid || hasErrorText,
                errorText
            });
        }
    }

    componentWillUnmount() {
        // Safety in case of render thrashing
        clearTimeout(this.timeout);
    }

    clearError = () => {
        this.setState({
            isInvalid: false,
            errorText: undefined
        });
    }

    handleNewErrorState = ({ isInvalid, errorText }) => {
        const { handleNewErrorState, id, name } = this.props;

        // bail if no handleNewErrorState exists
        if (!handleNewErrorState) {
            return;
        }

        // compare new state to old state. If different, invoke callback
        if (isInvalid !== this.errorState.isInvalid || errorText !== this.errorState.errorText) {
            handleNewErrorState({
                id,
                name,
                isInvalid,
                errorText
            });

            this.errorState = {
                isInvalid,
                errorText
            };
        }
    }

    handleChange = (event) => {
        event.persist();
        const value = event.target.value;
        const { onChange } = this.props;
        const { isPristine } = this.state;

        if (onChange) {
            onChange(event);
        }
       
        if (value && isPristine) {
            this.setState({ isPristine: false });
        }
    };

    handleBlur = (event) => {
        const { isRequired, onBlur, value } = this.props;
        const { isPristine, isInvalid, errorText } = this.state;

        if (isRequired && !isPristine) {
            // this.validate() performs the validation and handles errors, but we're
            // also returning a copy of the state for use in onBlur. This is to avoid
            // more complex setState callbacks and having to call onBlur inside
            // this.validate()
            const validationState = this.validate();

            if (onBlur) {
                onBlur(event, { value, ...validationState });
            }

            if (validationState.isInvalid && validationState.errorText) {
                announce(validationState.errorText, 'assertive');
            }
        }
        else if (onBlur) {
            onBlur(event, { value, isInvalid, errorText });
        }
    };

    validate = () => {
        const { isRequired, id, validator, value } = this.props;

        // Because there's tricky timing issues with setting state and returning
        // updated state values that a controlled component can use, we're using and
        // returning a local variable and setting state with that variable once at
        // the end.

        const validationState = {
            isInvalid: false,
            errorText: null,
            id
        };

        // When the field is empty
        if (!value) {
            if (isRequired) {
                const errorText = typeof this.props.label === 'string'
                    ? `${this.props.label} is required.`
                    : 'This field is required';
                validationState.isInvalid = true;
                validationState.errorText = errorText;
            }
            else {
                // Reset error state
                this.clearError();
            }
        }

        if(value && validator) {
            const parsed = validator(value);

            if(parsed.isValid) {
                validationState.isInvalid = false;
                validationState.errorText = null;
            } else {
                validationState.isInvalid = true;
                validationState.errorText = parsed.errorText || null;
            }
        }

        // Update local component state
        this.setState(validationState);
        this.handleNewErrorState(validationState);

        // Return values to be passed on to onBlur
        return validationState;
    };

    render() {
        const {
            id,
            label,
            isDisabled,
            value,
            isLabelVisible,
            placeholder,
            isRequired,
            isInvalid,
            errorText,
            onBlur,
            onChange,
            innerRef,
            validator,
            className,
            shouldFocusOnMount,
            shouldValidateOnUpdate,
            ...rest
        } = this.props;

        const { isInvalid: isInvalidState, errorText: errorTextState } = this.state;

        return (
            <div
                data-component="TextArea"
                className={cx('sarsa-field-outer-container', className)}>
                <Label
                    htmlFor={id}
                    isVisible={isLabelVisible}
                    isRequired={isRequired}>
                    {label}
                </Label>
                <div
                    className="sarsa-field-inner-container"
                    ref={this.inputWrapperRef}>

                    <div className="sarsa-field-input-wrapper">
                        <textarea
                            className={cx('sarsa-text-field-input', {
                                'is-invalid': isInvalidState
                            })}
                            onChange={this.handleChange}
                            onBlur={this.handleBlur}
                            id={id}
                            ref={innerRef}
                            disabled={isDisabled}
                            value={value}
                            placeholder={placeholder}
                            required={isRequired}
                            aria-invalid={isInvalidState}
                            aria-describedby={errorTextState ? `${id}-error` : null}
                            {...rest} />
                    </div>

                    {errorTextState && (
                        <div
                            id={`${id}-error`}
                            className="sarsa-field-error-message">
                            <span className="req" aria-hidden="true">*</span>
                            {errorTextState}
                        </div>
                    )}
                </div>
            </div>
        );
    }
}